import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoInfoCallout, DespachoSectionCard, DespachoSimpleTable } from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import { PERIOD_MODE_OPTIONS } from "@/modules/despacho/complianceInstanceHelpers";

export function TareasGeneratePanel({ readOnly = false, onGenerated }) {
  const [periodKey, setPeriodKey] = useState("");
  const [periodMode, setPeriodMode] = useState("current_month");
  const [preview, setPreview] = useState(null);
  const [previewState, setPreviewState] = useState("idle");
  const [generateBusy, setGenerateBusy] = useState(false);


  const runPreview = async () => {
    setPreviewState("loading");
    setPreview(null);
    try {
      const body = { confirm: false };
      if (periodKey.trim()) {
        body.period_key = periodKey.trim();
      } else {
        const params = periodMode === "next_month" ? {} : { upcoming: true };
        const instances = await despachoFiscalInfoApi.listComplianceInstances(params);
        const ids = instances.map((i) => i.id).slice(0, 500);
        if (!ids.length) {
          toast.message("No hay instancias fiscales para generar tareas.");
          setPreview({ tasks_to_create: [], summary: { new_count: 0, duplicate_count: 0 }, instances_selected: 0 });
          setPreviewState("ready");
          return;
        }
        body.compliance_instance_ids = ids;
      }
      const data = await despachoFiscalInfoApi.previewGenerateTasks(body);
      setPreview(data);
      setPreviewState("ready");
    } catch (err) {
      setPreviewState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  };

  const confirmGenerate = async () => {
    if (readOnly) return;
    setGenerateBusy(true);
    try {
      const uniqueInst = [
        ...new Set((preview?.tasks_to_create || []).map((t) => t.compliance_instance_id).filter(Boolean)),
      ];
      const result = await despachoFiscalInfoApi.generateTasks({
        confirm: true,
        compliance_instance_ids: uniqueInst.length ? uniqueInst : undefined,
        period_key: periodKey || undefined,
      });
      toast.success(`Se crearon ${result.created ?? 0} tarea(s).`);
      setPreview(null);
      setPreviewState("idle");
      onGenerated?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setGenerateBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="tareas-generar-panel">
      <DespachoInfoCallout>
        Genere tareas operativas desde instancias fiscales ya creadas en el calendario. La pestaña{" "}
        <strong>Fiscales</strong> sincroniza el mes actual al abrirse; use este panel para otros periodos o para
        revisar la vista previa antes de confirmar.
      </DespachoInfoCallout>

      <DespachoSectionCard title="Opciones">
        <div className="grid gap-4 sm:grid-cols-2 max-w-xl">
          <div className="space-y-2">
            <Label>Periodo instancias (opcional)</Label>
            <Select value={periodMode} onValueChange={setPeriodMode}>
              <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIOD_MODE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Periodo YYYY-MM (opcional)</Label>
            <input
              className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm"
              placeholder="2026-06"
              value={periodKey}
              onChange={(e) => setPeriodKey(e.target.value)}
            />
          </div>
        </div>
        <Button type="button" className="mt-4" onClick={runPreview} disabled={previewState === "loading"}>
          {previewState === "loading" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : "Vista previa"}
        </Button>
      </DespachoSectionCard>

      {preview ? (
        <DespachoSectionCard
          title="Vista previa tareas"
          description={`${preview.summary?.new_count ?? 0} nuevas · ${preview.summary?.duplicate_count ?? 0} duplicados · ${preview.instances_selected ?? 0} instancias`}
        >
          <DespachoSimpleTable
            columns={[
              { key: "task_name", label: "Tarea" },
              { key: "fiscal_client_id", label: "Cliente" },
              { key: "period", label: "Periodo" },
              { key: "due_date", label: "Fecha tarea" },
              { key: "assigned_to_user_id", label: "Auxiliar" },
            ]}
            rows={(preview.tasks_to_create || []).slice(0, 80).map((r, i) => ({ ...r, id: `p-${i}` }))}
            emptyLabel="No hay tareas nuevas."
          />
          {!readOnly ? (
            <Button type="button" className="mt-4" onClick={confirmGenerate} disabled={generateBusy || !(preview.tasks_to_create || []).length}>
              {generateBusy ? "Generando…" : "Confirmar generación"}
            </Button>
          ) : null}
        </DespachoSectionCard>
      ) : null}
    </div>
  );
}
